import { Controller, FormProvider, Resolver, SubmitHandler, useForm } from "react-hook-form";
import Select, { components, OptionProps, PlaceholderProps } from "react-select";
import { GET_CLASSROOM_LIST } from "../../AdminDashboard/api/AdminDashboardQuery";
import images from "../../../assets/images";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup';
import { ReactNode, useEffect, useState } from "react";
import { FETCH_POLICY_NO_CACHE, messages, TAB_MAX_WIDTH } from "../../../utils/constants";
import FileUpload from "../../../components/Popups/FileUpload";
import ButtonStyle from "../../../components/ButtonStyle/ButtonStyle";
import ConfirmationPopup from "../../../components/ConfirmationPopup/ConfirmationPopup";
import './AdminAnnouncementForm.scss';
import { CREATE_ANNOUNCEMENT, DELETE_ANNOUNCEMENT, GET_ALL_CLASSROOM_IDS, UPDATE_ANNOUNCEMENT } from "../api/AdminAnnouncementQuery";
import { classroomListVariables } from "../hooks/useClassroomOptionsPrefetch";
import { useLazyQuery, useMutation } from "@apollo/client";
import ToasterWithMessage from "../../../components/toaster";
import { AnnouncementObjectType } from "../AdminAnnouncement";
import { useWindowWidth } from "../../../components/AdminDashboardTabsContentHeader/TabsContentHeader";
import { trackButtonClick } from "../../../firebase/analyticsHelper";
import { ButtonClickEvents } from "../../../firebase/constants/events";

interface PropsType {
  setDisplayForm: any;
  displayForm: boolean;
  setClickAction?: any;
  clickAction?: any;
  singleAnnouncementDetail?: AnnouncementObjectType;
}

interface IFormInput {
  title: string;
  message: string;
  classroomIds: string[];
}

type ClassroomOption = { value: string; label: string };

const SELECT_ALL_VALUE = "ALL";
const SELECT_ALL_OPTION: ClassroomOption = { value: SELECT_ALL_VALUE, label: "Select All Classrooms" };

// Renders each option with a checkbox
const CheckboxOption = (props: OptionProps<ClassroomOption, true>) => (
  <components.Option {...props}>
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        style={{ cursor: "pointer", width: "14px", height: "14px", accentColor: "var(--color-steelblue)", margin: 0 }}
      />
      <span>{props.label}</span>
    </div>
  </components.Option>
);

// Shows the selected classroom count inside the input box
const CountPlaceholder = (props: PlaceholderProps<ClassroomOption, true>) => {
  const count = (props.selectProps as any).selectedCount as number;
  return (
    <components.Placeholder {...props}>
      {count > 0 ? `${count} classroom${count > 1 ? "s" : ""} selected` : "Select classrooms"}
    </components.Placeholder>
  );
};

const selectComponents = { Option: CheckboxOption, Placeholder: CountPlaceholder };

const schema = yup.object().shape({
  classroomIds: yup
    .array()
    .of(yup.string().required())
    .min(1, "Select at least one classroom")
    .required("Select at least one classroom"),
  title: yup
    .string()
    .required("Title is required")
    .trim()
    // .matches(patterns?.alphaNumericSpace, messages?.title)
    // .matches(patterns?.NotStartWithNumber,messages?.titleNotStartWithNumber)
    // .min(5,messages?.titleLength)
    .max(100, messages?.titleLength),
  message: yup
    .string()
    .trim()
    .required("Message is required")
});

const AdminAnnouncementForm = ({ setDisplayForm, displayForm, setClickAction, clickAction, singleAnnouncementDetail }: PropsType) => {
  const { backArrow, teacherDeleteIcon, DeleteGif, solidinterfaceplus, AnnouncementImage, AddSubjectIcon, ImagePlaceholder } = images;
  const [profileImageURL, setProfileImageURL] = useState<string>();
  const { success, error } = ToasterWithMessage();
  const [open, setOpen] = useState<boolean>(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
  const baseUrl = process.env.REACT_APP_FRONTEND_URL;
  const width = useWindowWidth();

  const methods = useForm<IFormInput>({
    resolver: yupResolver(schema) as Resolver<IFormInput>,
    defaultValues: {
      title: '',
      message: '',
      classroomIds: []
    },
  });

  const [classroomOptions, setClassroomOptions] = useState<ClassroomOption[]>([]);
  const [classroomTotalCount, setClassroomTotalCount] = useState<number>(0);
  const [classroomOffset, setClassroomOffset] = useState<number>(0);

  const toOptions = (nodes: { cId: string; cName: string }[] = []): ClassroomOption[] =>
    nodes.map((c) => ({ value: c.cId, label: c.cName }));

  // Paginated classroom options for the dropdown (loaded as the menu is scrolled)
  const [getAllClassroom, { loading: classroomLoading }] = useLazyQuery(GET_CLASSROOM_LIST, {
    // Cache-first: the announcement page prefetches the first page, so the options show up immediately
    fetchPolicy: "cache-first",
    onCompleted: (data) => {
      const nodes = data?.allClassrooms?.nodes || [];
      setClassroomOptions((prev) => [...prev, ...toOptions(nodes)]);
      setClassroomTotalCount(data?.allClassrooms?.totalCount || 0);
      setClassroomOffset((prev) => prev + nodes.length);
    },
    onError: (err) => {
      error(err?.message);
    }
  });

  // Fetches every classroom id, used by "Select All Classrooms"
  const [getEveryClassroom] = useLazyQuery(GET_ALL_CLASSROOM_IDS, {
    // Prefetched with the page, so "Select All Classrooms" resolves from the cache without a network call
    fetchPolicy: "cache-first",
    onError: (err) => {
      error(err?.message);
    }
  });

  const fetchClassrooms = (offset: number) =>
    getAllClassroom({ variables: classroomListVariables(offset) });

  useEffect(() => {
    fetchClassrooms(0);
  }, []);

  const hasMoreClassrooms = classroomOptions.length < classroomTotalCount;

  const loadMoreClassrooms = () => {
    if (hasMoreClassrooms && !classroomLoading) {
      fetchClassrooms(classroomOffset);
    }
  };

  // True while the selection is "Select All" (cleared as soon as an individual option is toggled)
  const [isSelectAllChosen, setIsSelectAllChosen] = useState<boolean>(false);

  // fresh = true bypasses the cache, so ids are current at the moment they are used
  const fetchAllClassroomIds = async (fresh = false): Promise<string[]> => {
    const result = await getEveryClassroom(fresh ? { fetchPolicy: "network-only" } : undefined);
    return (result?.data?.allClassrooms?.nodes || []).map((c: { cId: string }) => c.cId);
  };

  const getInitialClassroomIds = (): string[] =>
    singleAnnouncementDetail?.announcementClassroomsByAId?.nodes?.map((n) => n.cId) || [];

  const [editAnnouncement, { loading: editAnnouncementLoading }] = useMutation(UPDATE_ANNOUNCEMENT, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
      success("Announcement updated successfully")
      setDisplayForm(!displayForm);
      setClickAction('');
      reset();
    },
    onError: (err) => {
      error(err?.message);
    }
  });

  const [deleteAnnouncement, { loading: deleteAnnouncementLoading }] = useMutation(DELETE_ANNOUNCEMENT, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
      success("Announcement deleted successfully")
      setDisplayForm(!displayForm);
      setClickAction('');
      reset();
    },
    onError: (err) => {
      error(err?.message);
    }
  });

  const [createAnnouncement, { loading }] = useMutation(CREATE_ANNOUNCEMENT, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
      success("Announcement added successfully")
      setDisplayForm(!displayForm);
      setClickAction('');
      reset();
    },
    onError: (err) => {
      error(err?.message)
    }
  });

  useEffect(() => {
    if (clickAction === 'edit') {
        reset({
          title: singleAnnouncementDetail?.aOriginalTitle,
          message: singleAnnouncementDetail?.aMessage,
          classroomIds: getInitialClassroomIds(),
        });
        setProfileImageURL(singleAnnouncementDetail?.aUrl)
    }
  },[clickAction]);

  const { handleSubmit, setFocus, reset, register, formState: { errors } } = methods;
    //To check the datas are differ 
    function areValuesDifferent(data: IFormInput) {
      const initialValues = {
        title: singleAnnouncementDetail?.aOriginalTitle,
        message: singleAnnouncementDetail?.aMessage,
        profileImage: singleAnnouncementDetail?.aUrl
      };
      const initialClassroomIds = getInitialClassroomIds();
      if (
        data.classroomIds.length !== initialClassroomIds.length ||
        data.classroomIds.some((id) => !initialClassroomIds.includes(id))
      ) {
        return true;
      }
      // Compare classroomName, code, headTeacher and noofstudentaccess
      if (data.title !== initialValues.title) {
        return true;
      }
      if (data.message !== initialValues.message) {
        return true;
      }
      if (profileImageURL !== initialValues.profileImage) {
        return true;
      }
  
      return false;
    }
  const onSubmit: SubmitHandler<IFormInput> = async (formData) => {
    let data = formData;
    if (isSelectAllChosen) {
      // The ids picked by "Select All" may be stale by now (classrooms added or deleted since); re-fetch before sending
      try {
        data = { ...formData, classroomIds: await fetchAllClassroomIds(true) };
      } catch {
        data = formData;
      }
    }
    trackButtonClick(ButtonClickEvents?.CREATE_ANNOUNCEMENT_SAVE_BUTTON_CLICK, {
      description: "User clicks the save button on admin announcement form"
    });
    const variables = {
      input: {
          aTitle: data?.title?.trim(),
          aMessage: data?.message?.trim(),
          aUrl: profileImageURL,
          classroomIds: data?.classroomIds
        }
    };
    const editVariables = {
      aId: singleAnnouncementDetail?.aId,
      input: {
          aTitle: data?.title?.trim(),
          aMessage: data?.message?.trim(),
          aUrl: profileImageURL,
          classroomIds: data?.classroomIds
      }
    };
    
    if (clickAction === 'edit' && (areValuesDifferent(data)) && editAnnouncementLoading === false && !isUploadOpen) {
      editAnnouncement({
        variables: editVariables
      })
    } else if(clickAction === 'edit' && (!areValuesDifferent(data))){
      error("There is no changes to update");
    }else if (clickAction === 'add' && loading === false && !isUploadOpen) {
      createAnnouncement({
        variables
      })
    }
  }

  //To delete the announcement
  const handleDelete = (aId: string | undefined) => {
    if (deleteAnnouncementLoading === false) {
      setOpenConfirmationPopup(true);
    }
  };

  // click yes button in confirmation popup
  const onYesButtonClick = () => {
    setOpenConfirmationPopup(false);
    deleteAnnouncement({
      variables: {
        aId: singleAnnouncementDetail?.aId
      }
    });
  }

  // click no button in confirmation popup
  const onNoButtonClick = () => {
    setOpenConfirmationPopup(false);
  }

  useEffect(() => {
    setFocus('title');
  }, [setFocus]);

  return (
    <div className='adminAnnouncementForm'>
      <div className='adminAnnouncementFormHeader'>
        <img
          onClick={() => { 
            setDisplayForm(!displayForm); 
            setClickAction(''); 
            reset(); 
          }}
          className="backArrow"
          alt="back-arrow"
          src={backArrow}
        />
        {width <= TAB_MAX_WIDTH && <div className="title">Add Announcement</div>}
      </div>
      <div className="adminAnnouncementFormContainer">
        <FormProvider {...methods}>
          <form className="addAnnouncementForm" onSubmit={handleSubmit(onSubmit)}>
            <div className={`addAnnouncementParentContainer ${Object.keys(errors).length ? 'hasErrorStyle' : ''}`}>
              <div className="announcementDetails">
                <div className="announcementTitleField">
                  <label className="titleOfAnnouncement">Title</label>
                  <div>
                  <input {...register('title')} className="titleInputStyle" placeholder="Title" type="text" />
                  {errors.title && <span className="error-message">{errors.title?.message as ReactNode}</span>}
                  </div>
                </div>
                <div className="announcementMessageField">
                  <label className="messageOfAnnouncement">Message</label>
                  <div>
                  <textarea {...register('message')} className={profileImageURL ? "messageInputStyleWithImg" : "messageInputStyle"} placeholder="Message" />
                  {errors?.message && <span className="error-message">{errors.message?.message as ReactNode}</span>}
                  </div>
                </div>
                <div className="announcementMessageField">
                  <label className="messageOfAnnouncement">Classrooms</label>
                  <div>
                    <Controller
                      name="classroomIds"
                      control={methods.control}
                      render={({ field }) => {
                        const selectedIds: string[] = field.value || [];
                        const isAllSelected = classroomTotalCount > 0 && selectedIds.length === classroomTotalCount;
                        return (
                          <Select<ClassroomOption, true>
                            isMulti
                            isSearchable={false}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            controlShouldRenderValue={false}
                            isLoading={classroomLoading}
                            components={selectComponents}
                            className={`classroomSelect ${selectedIds.length > 0 ? "hasSelection" : ""}`}
                            classNamePrefix="classroomSelect"
                            {...({ selectedCount: selectedIds.length } as any)}
                            options={[SELECT_ALL_OPTION, ...classroomOptions]}
                            // Selection is tracked in the form value; the checkbox state is derived from it
                            value={[]}
                            isOptionSelected={(option) =>
                              option.value === SELECT_ALL_VALUE ? isAllSelected : selectedIds.includes(option.value)
                            }
                            onMenuScrollToBottom={loadMoreClassrooms}
                            onChange={async (_, meta) => {
                              const clicked = (meta as any).option as ClassroomOption | undefined;
                              if (!clicked) return;
                              if (clicked.value === SELECT_ALL_VALUE) {
                                // Select All sends every classroom id; clicking it again clears the selection
                                setIsSelectAllChosen(!isAllSelected);
                                field.onChange(isAllSelected ? [] : await fetchAllClassroomIds());
                              } else if (selectedIds.includes(clicked.value)) {
                                setIsSelectAllChosen(false);
                                field.onChange(selectedIds.filter((id) => id !== clicked.value));
                              } else {
                                setIsSelectAllChosen(false);
                                field.onChange([...selectedIds, clicked.value]);
                              }
                            }}
                          />
                        );
                      }}
                    />
                    {errors?.classroomIds && <span className="error-message">{errors.classroomIds?.message as ReactNode}</span>}
                  </div>
                </div>
                <div className="announcementProfileImage">
                  <div 
                    onClick={() => {
                      setIsUploadOpen(true); 
                      setOpen(true);
                    }} 
                    className="uploadPhoto"
                  >
                    <span className="addPhoto">Add Photo</span>
                    <img
                      className="uploadButton"
                      alt="plus"
                      src={width <= TAB_MAX_WIDTH ? AddSubjectIcon : solidinterfaceplus}
                    />
                  </div>
                  {/* <div className={`${profileImageURL ? 'announcementEditContainer' : 'teacherImageContainer'}`}> */}
                   {profileImageURL 
                   ? <img 
                        className={`${profileImageURL ? 'announcementImageProfileStyle' : 'announcementImageDefault'}`} 
                        alt='AnnouncementImage' 
                        src={profileImageURL ? baseUrl + profileImageURL : AnnouncementImage} 
                      />
                   : width <= TAB_MAX_WIDTH 
                      ? <img src={ImagePlaceholder} width="120px" height="120px" className="image-placeholder" alt="Image Placeholder" /> 
                      : <></>
                  }
                  {/* </div> */}
                  {
                    isUploadOpen ?
                      <FileUpload 
                          page="Admin Announcement" 
                          type='profile' 
                          setUrl={setProfileImageURL} 
                          open={open} 
                          setOpen={setOpen} 
                          setIsUploadOpen={setIsUploadOpen} 
                          folderName="images" 
                      />
                      : null
                  }
                </div>
                {width <= TAB_MAX_WIDTH && (
                  clickAction !== 'edit' ?
                    <ButtonStyle 
                        paddingLeft='16px' 
                        paddingRight='15px' 
                        width='62px' 
                        height='23.92px' 
                        color='var(--color-black)' 
                        bgcolor="white" 
                        variant='outlined' 
                        text="Clear" 
                        onClick={() => { 
                          trackButtonClick(ButtonClickEvents?.CREATE_ANNOUNCEMENT_CLEAR_BUTTON_CLICK, { description: "User clicks the clear button on admin announcement form" }); 
                          reset(); 
                          setProfileImageURL(undefined); 
                        }} 
                        type="reset" 
                      />
                    : <ButtonStyle 
                        paddingLeft='16px' 
                        paddingRight='15px' 
                        width='62px' 
                        height='23.92px' 
                        color='var(--color-black)' 
                        bgcolor="white" 
                        variant='outlined' 
                        disableButton={deleteAnnouncementLoading||isUploadOpen} 
                        onClick={() => handleDelete(singleAnnouncementDetail?.aId)} 
                        deleteBtn={<img src={teacherDeleteIcon} alt="deleteIcon" className='deleteIconStyle' />} 
                      />
                )}
              </div>
              <div className="announcementFormButton">
                {width > TAB_MAX_WIDTH && (
                  clickAction !== 'edit' ?
                    <ButtonStyle 
                        paddingLeft='16px' 
                        paddingRight='15px' 
                        width='62px' 
                        height='23.92px' 
                        color='var(--color-black)' 
                        bgcolor="white" 
                        variant='outlined' 
                        text="Clear" 
                        onClick={() => { 
                          reset(); 
                          setProfileImageURL(undefined); 
                        }} 
                        type="reset" 
                    />
                    : <ButtonStyle 
                        paddingLeft='16px' 
                        paddingRight='15px' 
                        width='62px' 
                        height='23.92px' 
                        color='var(--color-black)' 
                        bgcolor="white" 
                        variant='outlined' 
                        disableButton={deleteAnnouncementLoading||isUploadOpen} 
                        onClick={() => handleDelete(singleAnnouncementDetail?.aId)} 
                        deleteBtn={<img src={teacherDeleteIcon} alt="deleteIcon" className='deleteIconStyle' />} 
                      />
                )}
                <ButtonStyle 
                    paddingLeft='26px' 
                    paddingRight='28px' 
                    width='83.25px' 
                    height='23.92px' 
                    disableButton={loading || editAnnouncementLoading||isUploadOpen} 
                    text="Save" 
                    type="submit" 
                />
              </div>
            </div>
          </form>
          <ConfirmationPopup
            open={openConfirmationPopup}
            message={`Are you sure you want to Delete this announcement?`}
            gifData={DeleteGif}
            yesButtonFunctionality={onYesButtonClick}
            noButtonFunctionality={onNoButtonClick}
            messageWidth={'195px'}
          />
        </FormProvider>
      </div>
    </div>
  )
}

export default AdminAnnouncementForm;
